// PlaybookItemFocus.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsPlaybookFocusEmbed } from 'hooks/useDeviceDetect';

// Hooks
import usePlaybookItemFocusCatalog from 'node/missions/hooks/usePlaybookItemFocusCatalog';
import useWorkflowStepItemAttachments from 'node/missions/hooks/useWorkflowStepItemAttachments';
import useWorkflowTaskChecklist from 'node/missions/hooks/useWorkflowTaskChecklist';

// Utilities
import {
  getPlaybookCurrentStepIndex,
  getPlaybookFocusSteps,
} from 'node/missions/mission/focus/playbookFocusUtils';
import {
  getPlaybookCurrentItemIndex,
  getPlaybookItemFocusEntryKey,
  getPlaybookItemFocusItemLabel,
  getPlaybookItemFocusPeekLabel,
  getPlaybookItemFocusStepLabel,
  isPlaybookItemFocusDone,
} from 'node/missions/mission/focus/playbookItemFocusUtils';
import {
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
  MISSION_TASK_HANDLED_BY,
} from 'missions/taskmodal/missionTaskModalUtils';
import { getMissionProgress } from 'node/missions/mission/utils/missionRunUtils';
import { applyWorkflowStepNodeToRunTask } from 'node/missions/mission/utils/workflowStepNodeUtils';
import {
  getChecklistActionButtonLabel,
  getChecklistActionIconKey,
  isChecklistSetFieldActionType,
} from 'missions/taskmodal/checklistItemActions';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// CSS
import './PlaybookItemFocus.css';

// Components
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import NodeHiddenFileInputs from 'node/inputs/NodeHiddenFileInputs';
import NodeMissionTaskChecklistActionModal from 'node/missions/mission/task/checklist/NodeMissionTaskChecklistActionModal';
import PlaybookItemFocusAttachments from 'node/missions/mission/focus/PlaybookItemFocusAttachments';
import ProfileStack from 'components/profilestack/ProfileStack';

/**
 * PlaybookItemFocus
 *
 * Checklist-item playbook wizard. Lands on the current subtask and
 * walks items with previous/next. Hosted in a desktop modal or as
 * the mobile playbook chrome.
 *
 * @param {boolean} [active=true] - When false, skip follow-current resets.
 * @param {object|null} [hostNode] - Workflow host record.
 * @param {object|null} missionActions - Shared run action handlers.
 * @param {object|null} [missionProgress] - Optional precomputed progress.
 * @param {object|null} missionRun - Normalized playbook run.
 * @param {function} [onHostNodeChange] - Optional local host patch after field saves.
 * @param {Map<string, object>|null} [stepNodesByKey] - Live TASK nodes keyed by nodeKey.
 * @param {object|null} template - Playbook template.
 * @param {object|null} user - Current signed-in user.
 * @returns {ReactNode} PlaybookItemFocus.
 */
const PlaybookItemFocus = React.memo(({
  active = true,
  hostNode = null,
  missionActions = null,
  missionProgress = null,
  missionRun = null,
  onHostNodeChange = null,
  stepNodesByKey = null,
  template = null,
  user = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isCompactFocus = useIsPlaybookFocusEmbed();

  // ----- STATE ----- //

  const [browseIndex, setBrowseIndex] = useState(0);
  const [followCurrent, setFollowCurrent] = useState(true);
  const previousFocusRef = useRef({ key: '', done: false });

  // ----- HOOKS ----- //

  const { entries, statusByKey } = usePlaybookItemFocusCatalog({
    missionRun,
    stepNodesByKey,
    template,
  });

  // ----- MEMOS ----- //

  const currentStepIndex = useMemo(
    () => getPlaybookCurrentStepIndex(getPlaybookFocusSteps(missionRun)),
    [missionRun]
  );

  const currentIndex = useMemo(
    () => getPlaybookCurrentItemIndex(entries, statusByKey, currentStepIndex),
    [currentStepIndex, entries, statusByKey]
  );

  const resolvedProgress = useMemo(
    () => missionProgress || getMissionProgress(missionRun),
    [missionProgress, missionRun]
  );

  const focusIndex = useMemo(() => {
    if (!entries.length) return -1;
    if (followCurrent) return currentIndex;
    return Math.max(0, Math.min(browseIndex, entries.length - 1));
  }, [browseIndex, currentIndex, entries.length, followCurrent]);

  const focusEntry = focusIndex >= 0 ? entries[focusIndex] || null : null;
  const previousEntry = focusIndex > 0 ? entries[focusIndex - 1] || null : null;
  const nextEntry = focusIndex >= 0 && focusIndex < entries.length - 1
    ? entries[focusIndex + 1] || null
    : null;

  const handleRequestCompleteStep = useCallback(() => {
    if (!template || !missionRun || !focusEntry?.task?.key) return;
    missionActions?.handleTaskStatusChange?.(
      template,
      missionRun,
      focusEntry.task.key,
      'complete',
    );
  }, [focusEntry?.task?.key, missionActions, missionRun, template]);

  const focusTask = useMemo(
    () => applyWorkflowStepNodeToRunTask(
      focusEntry?.task || null,
      focusEntry?.stepNode || null,
    ),
    [focusEntry?.stepNode, focusEntry?.task]
  );

  const assigneeProfiles = useMemo(() => {
    if (focusTask?.handledBy === MISSION_TASK_HANDLED_BY.BOT) return [];

    return (Array.isArray(focusTask?.assignees) ? focusTask.assignees : [])
      .map((assignee) => {
        const key = getMissionTaskPersonKey(assignee);
        if (!key) return null;

        const label = getMissionTaskPersonLabel(assignee) || key;
        const title = String(assignee?.title || '').trim() || label;

        return {
          key,
          userKey: key,
          name: label,
          title,
          coverUrl: assignee?.coverUrl || '',
          data_profile: {
            name: label,
            email: assignee?.email || assignee?.data_profile?.email || '',
          },
        };
      })
      .filter(Boolean);
  }, [focusTask?.assignees, focusTask?.handledBy]);

  const assigneeNameList = useMemo(() => (
    assigneeProfiles
      .map((profile) => profile.title || profile.name)
      .filter(Boolean)
      .join(', ')
  ), [assigneeProfiles]);

  const checklist = useWorkflowTaskChecklist({
    hostNode,
    onRequestComplete: handleRequestCompleteStep,
    stepNode: focusEntry?.stepNode || null,
    task: focusTask,
    template,
    user,
  });

  const snapshotItem = useMemo(
    () => checklist.findSnapshotItem(focusEntry?.itemKey),
    [checklist, focusEntry?.itemKey]
  );
  const itemText = String(snapshotItem?.text || focusEntry?.text || '').trim()
    || 'Untitled item';
  const isDone = snapshotItem
    ? String(snapshotItem.status || '').toLowerCase() === 'done'
    : isPlaybookItemFocusDone(focusEntry, statusByKey);
  const actions = snapshotItem
    ? checklist.getItemActions(snapshotItem)
    : checklist.getItemActions({ key: focusEntry?.itemKey });
  const resources = snapshotItem
    ? checklist.getItemResources(snapshotItem)
    : checklist.getItemResources({ key: focusEntry?.itemKey });
  const itemAttachments = useWorkflowStepItemAttachments({
    itemKey: focusEntry?.itemKey,
    stepNode: focusEntry?.stepNode || null,
    user,
  });

  const stepLabel = getPlaybookItemFocusStepLabel(focusEntry);
  const itemLabel = getPlaybookItemFocusItemLabel(focusEntry);
  const canToggle = Boolean(snapshotItem?.key);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!active) {
      previousFocusRef.current = { key: '', done: false };
      return;
    }

    setFollowCurrent(true);
  }, [active]);

  useEffect(() => {
    if (!active || !followCurrent) return;
    setBrowseIndex(Math.max(0, currentIndex));
  }, [active, currentIndex, followCurrent]);

  useEffect(() => {
    if (!active) return;

    const focusKey = getPlaybookItemFocusEntryKey(focusEntry);
    const previous = previousFocusRef.current;
    previousFocusRef.current = { key: focusKey, done: isDone };

    if (!previous.key || previous.key !== focusKey) return;
    if (previous.done || !isDone) return;

    setFollowCurrent(true);
  }, [active, focusEntry, isDone]);

  // ----- HANDLERS ----- //

  /**
   * handlePrevious
   *
   * Browses to the previous checklist item on the path.
   */
  const handlePrevious = useCallback(() => {
    if (focusIndex <= 0) return;
    setFollowCurrent(false);
    setBrowseIndex(focusIndex - 1);
  }, [focusIndex]);

  /**
   * handleNext
   *
   * Browses to the next checklist item on the path.
   */
  const handleNext = useCallback(() => {
    if (focusIndex < 0 || focusIndex >= entries.length - 1) return;
    setFollowCurrent(false);
    setBrowseIndex(focusIndex + 1);
  }, [entries.length, focusIndex]);

  const canAdvance = focusIndex >= 0 && focusIndex < entries.length - 1;
  const canComplete = canToggle && !isDone;
  const canSkip = canComplete && canAdvance;

  /**
   * handleForward
   *
   * Completes the focused item when it is still open, otherwise
   * browses to the next item. Completing snaps back to current.
   */
  const handleForward = useCallback(() => {
    const snapshotKey = String(snapshotItem?.key || '').trim();
    if (canComplete && snapshotKey) {
      checklist.handleToggleItem(snapshotKey);
      return;
    }
    handleNext();
  }, [canComplete, checklist, handleNext, snapshotItem?.key]);

  if (!template) return null;

  // ----- RENDER ----- //

  return (
    <div className="playbook-item-focus">
      <ModalForm containScroll={true} gap="16px" height="100%">

        <div className="playbook-item-focus-container">

          {/* PROGRESS */}
          <div className="playbook-item-focus-progress">
            <div
              className="playbook-item-focus-progress-fill"
              style={{
                backgroundColor: theme.green,
                width: `${resolvedProgress?.percent || 0}%`,
              }}
            />
          </div>

          {/* POSITION */}
          {stepLabel ? (
            <div className="playbook-item-focus-step">
              {stepLabel}
            </div>
          ) : null}

          {itemLabel || (isCompactFocus && assigneeProfiles.length > 0) ? (
            <div className="playbook-item-focus-item-count">
              <span>{itemLabel}</span>
              {isCompactFocus && assigneeProfiles.length > 0 ? (
                <div
                  className="playbook-item-focus-item-count-assignees"
                  title={assigneeNameList}
                >
                  <ProfileStack
                    allowNavigation={false}
                    maxDisplay={5}
                    overlap={8}
                    profiles={assigneeProfiles}
                    size={22}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {!focusEntry ? (
            <ModalHint hint="This playbook has no checklist items to show." />
          ) : (
            <div className="playbook-item-focus-stage">

              {/* ASSIGNEES — desktop stage; mobile sits on the item-count row */}
              {!isCompactFocus && assigneeProfiles.length > 0 ? (
                <div
                  className="playbook-item-focus-assignees"
                  title={assigneeNameList}
                >
                  <ProfileStack
                    allowNavigation={false}
                    maxDisplay={5}
                    overlap={12}
                    profiles={assigneeProfiles}
                    size={64}
                  />
                </div>
              ) : null}

              {/* ITEM */}
              <div className="playbook-item-focus-copy">
                {itemText}
              </div>

              {/* ACTIONS */}
              {actions.length > 0 || resources.length > 0 ? (
                <div className="playbook-item-focus-actions">
                  {actions.map((action) => {
                    const actionLabel = getChecklistActionButtonLabel(
                      action,
                      checklist.modalFields,
                    );
                    const isRequiredValue = action.requireValue === true
                      && isChecklistSetFieldActionType(action?.type);
                    return (
                      <Button
                        key={action.id}
                        enabled={checklist.isActionEnabled(action)}
                        iconKey={getChecklistActionIconKey(action)}
                        label={isRequiredValue ? `${actionLabel} *` : actionLabel}
                        padding="10px 28px"
                        title={isRequiredValue
                          ? 'Required — set a value before checking this item off'
                          : undefined}
                        width="100%"
                        onClick={() => checklist.handleOpenAction(
                          action,
                          focusEntry?.itemKey,
                        )}
                      />
                    );
                  })}
                  {resources.map((resource) => {
                    const liveNode = checklist.resourceNodesByKey.get(resource.nodeKey);
                    const typeDefForResource = liveNode?.type
                      ? getNodeType(liveNode.type)
                      : null;
                    const { iconKey } = resolveNodeIcon({
                      node: liveNode || null,
                      typeDef: typeDefForResource,
                    });
                    const label = String(
                      liveNode?.title || resource.title || 'Resource',
                    ).trim() || 'Resource';

                    return (
                      <Button
                        key={resource.id}
                        iconKey={iconKey || 'bookmark'}
                        label={label}
                        padding="10px 28px"
                        width="100%"
                        onClick={() => checklist.handleOpenResource(resource)}
                      />
                    );
                  })}
                </div>
              ) : null}

              <PlaybookItemFocusAttachments attachments={itemAttachments} />


            </div>
          )}

          {/* NEIGHBORS — desktop modal only; mobile needs the stage height */}
          {!isCompactFocus && entries.length > 1 ? (
            <div className="playbook-item-focus-neighbors">
              <div className="playbook-item-focus-neighbor is-previous">
                {previousEntry
                  ? getPlaybookItemFocusPeekLabel(previousEntry, statusByKey, 'previous')
                  : 'Start of playbook'}
              </div>
              <div className="playbook-item-focus-neighbor is-next">
                {nextEntry
                  ? getPlaybookItemFocusPeekLabel(nextEntry, statusByKey, 'next')
                  : 'End of playbook'}
              </div>
            </div>
          ) : null}
        </div>
      </ModalForm>

      <ModalButtons
        style={{
          justifyContent: 'space-between',
        }}
      >

        {/* PREVIOUS */}
        <Button
          enabled={focusIndex > 0}
          iconKey="chevron_left"
          label="Previous"
          onClick={handlePrevious}
        />

        <div className="playbook-item-focus-forward">

          {/* SKIP */}
          {canSkip ? (
            <Button
              backgroundColor="transparent"
              foregroundColor={theme.foregroundMuted}
              hoverBackgroundColor={theme.backgroundHover}
              hoverForegroundColor={theme.foreground}
              label="Skip"
              title="Leave this item open and go to the next one"
              onClick={handleNext}
            />
          ) : null}

          {/* NEXT */}
          <Button
            enabled={canComplete || canAdvance}
            endIconKey="chevron_right"
            label={canComplete ? 'Complete' : 'Next'}
            onClick={handleForward}
          />
        </div>
      </ModalButtons>

      <Confirm
        confirmLabel="Mark complete"
        isOpen={checklist.confirmOpen}
        message="All checklist items are done. Would you like to mark this step complete?"
        secondaryLabel="Not yet"
        setOpen={checklist.setConfirmOpen}
        title="Checklist complete"
        onConfirm={checklist.handleConfirmComplete}
        onSecondary={checklist.handleDismissComplete}
      />

      <NodeMissionTaskChecklistActionModal
        fieldDef={checklist.activeAction?.fieldDef || null}
        hostNode={hostNode}
        isOpen={Boolean(checklist.activeAction)}
        onClose={checklist.handleCloseAction}
        onHostNodeChange={onHostNodeChange}
      />

      <NodeHiddenFileInputs
        documentInputRef={checklist.documentInputRef}
        filesInputRef={checklist.filesInputRef}
        photoInputRef={checklist.photoInputRef}
        videoInputRef={checklist.videoInputRef}
        onDocumentSelect={checklist.handleDocumentSelect}
        onFilesSelect={checklist.handleFilesSelect}
        onPhotoSelect={checklist.handlePhotoSelect}
        onVideoSelect={checklist.handleVideoSelect}
      />

      <CameraModal
        contentType={checklist.cameraContentType}
        isOpen={checklist.cameraOpen}
        setOpen={checklist.setCameraOpen}
        onContentCreated={checklist.handleCameraContentCreated}
      />
    </div>
  );
});

// Dev Tools
PlaybookItemFocus.displayName = 'PlaybookItemFocus';

export default PlaybookItemFocus;
